import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export declare class HamCheckboxChange {
    source: any;
    checked: boolean;
}

@Directive({
    selector: '[hamCheckbox]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => HamCheckboxDirective),
            multi: true,
        },
    ],
})
export class HamCheckboxDirective implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor {
    // Static list to track all checkboxes
    static instances: HamCheckboxDirective[] = [];

    @HostBinding('class') get className(): string {
        // Dynamically apply classes based on the active state
        return `ham-checkbox${this.checked ? ' active' : ''}${this.disabled ? ' disabled' : ''}`;
    }

    @Input() checked: boolean = false;
    @Input() disabled: boolean = false;
    @Input() alwaysUnchecked: boolean = false;
    @Input() bubbleEvents: boolean = false;

    /** If true, enables single selection behavior */
    @Input() single: boolean = false;

    @Output() change = new EventEmitter<HamCheckboxChange>();

    private listeners: (() => void)[] = [];
    private checkBoxIcon: HTMLElement;

    constructor(private el: ElementRef, private renderer: Renderer2) {}

    ngOnInit() {
        HamCheckboxDirective.instances.push(this);
        this.listeners.push(this.renderer.listen(this.el.nativeElement, 'click', (event) => this.onClick(event)));
    }

    ngAfterViewInit() {
        this.createCheckboxIcon();
    }

    ngOnDestroy() {
        this.listeners.forEach((listener) => listener());
        HamCheckboxDirective.instances = HamCheckboxDirective.instances.filter((cb) => cb !== this);
    }

    private createCheckboxIcon() {
        // Create the <span> element
        const spanElement = this.renderer.createElement('span');
        this.renderer.addClass(spanElement, 'content-wrapper');

        // Move the innerText from the host element to the <span>
        spanElement.innerHTML = this.el.nativeElement.innerHTML;

        // Clear the innerHTML of the host element
        this.renderer.setProperty(this.el.nativeElement, 'innerHTML', '');

        // Create the <i> element for the checkbox icon
        const checkBoxWrapper = this.renderer.createElement('span');
        this.renderer.addClass(checkBoxWrapper, 'checkbox-wrapper');
        this.checkBoxIcon = this.renderer.createElement('i');

        // Append the <i> element after first
        this.renderer.appendChild(checkBoxWrapper, this.checkBoxIcon);
        this.renderer.appendChild(this.el.nativeElement, checkBoxWrapper);

        // Insert the <span> as the last child of the host element
        this.renderer.appendChild(this.el.nativeElement, spanElement);
    }

    private onClick(event: MouseEvent) {
        if (!this.bubbleEvents) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (this.disabled) return;

        if (this.single && !this.checked && !this.alwaysUnchecked) {
            // Single-selection mode: uncheck all others
            HamCheckboxDirective.instances.forEach((instance) => {
                if (instance !== this && instance.single && instance.checked) {
                    instance.checked = false;
                    instance.change.emit({ source: instance, checked: false });
                }
            });
            this.checked = true;
            this.onChange(this.checked);
            this.onTouched();
        } else if (!this.alwaysUnchecked) {
            // Multi-select toggle
            this.checked = !this.checked;
            this.onChange(this.checked);
            this.onTouched();
        }

        this.change.emit({ source: this, checked: this.checked });
    }

    private onChange: (_: any) => void = () => {};
    private onTouched: () => void = () => {};

    writeValue(value: any): void {
        this.checked = !!value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
